import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import cn from 'classnames';
import Hls from 'hls.js';

import CloudflareStreamThumbnail from 'coreClasses/cloudflare-stream/CloudflareStreamThumbnail';

import {useCarouselControllerContext} from 'brandComponents/Carousel';
import Picture from 'brandComponents/Picture';

import {ICloudflareStreamVideoProps} from './ICloudflareStreamVideo';
import {MuteButton, PlayButton} from './Subcomponents';

import styles from './CloudflareStreamVideo.module.scss';

const CLIENT_BANDWIDTH_HINT = 10;

const CloudflareStreamVideo = ({cloudflareStreamVideo, videoConfig, controlsConfig}: ICloudflareStreamVideoProps) => {
	const carouselContext = useCarouselControllerContext(true);
	const isInCarousel = carouselContext?.isInCarousel;
	const carouselHasLoop = carouselContext?.swiperRef?.current?.params.loop;

	const {showControls = false, autoplay = true, muted = true, loop = true} = videoConfig;
	const isAutoplay = autoplay && muted;

	const wrapperRef = useRef(null);
	const thumbnailRef = useRef(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const hlsRef = useRef<Hls>(null);

	const videoId = cloudflareStreamVideo.getVideoId();
	const manifestUrl = useMemo(
		() => `https://videodelivery.net/${videoId}/manifest/video.m3u8?clientBandwidthHint=${CLIENT_BANDWIDTH_HINT}`,
		[videoId]
	);

	const thumbnailImage = useMemo(
		() => new CloudflareStreamThumbnail(videoId, cloudflareStreamVideo.getCustomThumbnail()),
		[cloudflareStreamVideo, videoId]
	);

	const [isVisible, setIsVisible] = useState<boolean>(false);
	const [initializeVideo, setInitializeVideo] = useState<boolean>(false);
	const [isPlaying, setIsPlaying] = useState<boolean>(isAutoplay);
	const [isMuted, setIsMuted] = useState<boolean>(muted);
	const aspectRatio = useMemo(() => cloudflareStreamVideo.getAspectRatio() || '16/9', [cloudflareStreamVideo]);

	const toggleThumbnail = useCallback((show) => {
		thumbnailRef.current.classList.toggle('d-none', !show);
	}, []);

	useEffect(() => {
		if (!initializeVideo || !videoRef.current) {
			return;
		}

		const video = videoRef.current;

		if (Hls.isSupported()) {
			const hls = new Hls({startLevel: -1});
			hls.loadSource(manifestUrl);
			hls.attachMedia(video);
			hlsRef.current = hls;

			hls.on(Hls.Events.MANIFEST_PARSED, () => {
				if (isAutoplay) {
					video.play().catch(() => {});
				}
			});

			return () => {
				hls.destroy();
				hlsRef.current = null;
			};
		}

		if (video.canPlayType('application/vnd.apple.mpegurl')) {
			video.src = manifestUrl;

			if (isAutoplay) {
				video.play().catch(() => {});
			}
		}
	}, [initializeVideo, manifestUrl]);

	useEffect(() => {
		setIsPlaying(isVisible && autoplay);
	}, [isVisible]);

	useEffect(() => {
		if (videoRef.current) {
			if (isPlaying) {
				videoRef.current.play().catch(() => {});
			} else {
				videoRef.current.pause();
			}
		}
	}, [isPlaying]);

	useEffect(() => {
		if (videoRef.current) {
			videoRef.current.muted = isMuted;
		}
	}, [isMuted]);

	useEffect(() => {
		const options = {
			threshold: 0.1
		};

		const observer = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting) {
				setIsVisible(true);
				setInitializeVideo(true);
			} else {
				setIsVisible(false);

				if (isInCarousel && carouselHasLoop) {
					setInitializeVideo(false);
					toggleThumbnail(true);
				}
			}
		}, options);

		if (wrapperRef.current) {
			observer.observe(wrapperRef.current);
		}

		return () => {
			if (wrapperRef.current) {
				observer.unobserve(wrapperRef.current);
			}
		};
	}, [isInCarousel, carouselHasLoop]);

	return (
		<div className={cn('cf-stream-video-wrapper', styles['cf-stream-video-wrapper'])} ref={wrapperRef}>
			<div className={cn('cf-stream-video-thumbnail', styles['cf-stream-video-thumbnail'])} ref={thumbnailRef}>
				<Picture image={thumbnailImage} showPlaceholder={false} classNames={styles['cf-stream-video-thumbnail__picture']} />
			</div>
			<div className={cn(styles['video-embed'], 'video-embed')} style={{aspectRatio}}>
				{initializeVideo &&
					<video
						ref={videoRef}
						controls={!controlsConfig && showControls}
						muted={isMuted}
						loop={loop}
						playsInline
						onLoadedData={() => toggleThumbnail(false)}
						style={{width: '100%', height: '100%', objectFit: 'cover'}}
					/>
				}
			</div>
			{(showControls && controlsConfig) && (
				<div className={cn('cf-stream-video-controls', styles['cf-stream-video-controls'])}>
					{controlsConfig?.playButton && (
						<PlayButton
							setIsPlaying={setIsPlaying}
							isPlaying={isPlaying}
							icons={{
								play: controlsConfig.playButton.playIcon,
								pause: controlsConfig.playButton.pauseIcon,
								height: controlsConfig.playButton.height,
								width: controlsConfig.playButton.width,
							}}
						/>
					)}
					{controlsConfig?.muteButton && (
						<MuteButton
							setIsMuted={setIsMuted}
							isMuted={isMuted}
							icons={{
								muted: controlsConfig.muteButton.mutedIcon,
								unmuted: controlsConfig.muteButton.unmutedIcon,
								height: controlsConfig.muteButton.height,
								width: controlsConfig.muteButton.width,
							}}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default CloudflareStreamVideo;
